import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Switch,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
} from '@mui/material';
import {
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const UserIngestionPanel = ({ userId, userData, onUpdate, sx: sxProp }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [ingestionData, setIngestionData] = useState({
    paused: false,
    reason: '',
    pausedAt: null,
  });
  
  const [editReason, setEditReason] = useState('');

  // Load ingestion status
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getUserIngestion(userId);
        setIngestionData({
          paused: response.data.ingestionPaused || false,
          reason: response.data.reason || '',
          pausedAt: response.data.pausedAt,
        });
        setEditReason(response.data.reason || '');
      } catch (err) {
        setError('Failed to load ingestion status');
      } finally {
        setLoading(false);
      }
    };
    
    if (userId) {
      loadData();
    }
  }, [userId]);

  // Toggle ingestion
  const handleToggle = async () => {
    const newPaused = !ingestionData.paused;
    
    try {
      setSaving(true);
      setError('');
      
      await adminAPI.updateUserIngestion(userId, {
        paused: newPaused,
        reason: newPaused ? editReason : null,
      });
      
      setIngestionData({
        ...ingestionData,
        paused: newPaused,
        reason: newPaused ? editReason : '',
        pausedAt: newPaused ? new Date().toISOString() : null,
      });
      
      setSuccess(newPaused ? 'User ingestion paused' : 'User ingestion resumed');
      
      if (onUpdate) {
        onUpdate({ ingestion_paused: newPaused });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update ingestion status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, ...sxProp }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, ...sxProp }}>
      <Typography variant="h6" gutterBottom>
        Message Processing
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {ingestionData.paused ? (
            <PauseIcon color="warning" fontSize="large" />
          ) : (
            <PlayIcon color="success" fontSize="large" />
          )}
          <Box>
            <Typography fontWeight={500}>
              Ingestion {ingestionData.paused ? 'Paused' : 'Active'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ingestionData.paused 
                ? 'Messages from this user are not being processed'
                : 'Messages from this user are being processed normally'
              }
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={ingestionData.paused ? 'PAUSED' : 'ACTIVE'}
          color={ingestionData.paused ? 'warning' : 'success'}
        />
      </Box>
      
      {ingestionData.paused && ingestionData.pausedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Paused {formatDistanceToNow(new Date(ingestionData.pausedAt), { addSuffix: true })}
          {ingestionData.reason && ` — "${ingestionData.reason}"`}
        </Typography>
      )}
      
      {!ingestionData.paused && (
        <TextField
          fullWidth
          size="small"
          label="Pause reason (optional)"
          value={editReason}
          onChange={(e) => setEditReason(e.target.value)}
          placeholder="e.g., Abuse detected, Testing, Cost concerns..."
          sx={{ mb: 2 }}
        />
      )}
      
      <Button
        variant={ingestionData.paused ? 'contained' : 'outlined'}
        color={ingestionData.paused ? 'success' : 'warning'}
        onClick={handleToggle}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} /> : (ingestionData.paused ? <PlayIcon /> : <PauseIcon />)}
      >
        {ingestionData.paused ? 'Resume Ingestion' : 'Pause Ingestion'}
      </Button>
    </Paper>
  );
};

export default UserIngestionPanel;
